import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useState, useSyncExternalStore } from "react";
import { WidgetAction } from "./actions";
import { createInitialState, widgetReducer } from "./reducer";
import { State } from "./state";
import { breakdownOf } from "./flows";

/**
 * The donation widget's state: a reducer (reducer.ts) behind a small store that React reads with
 * useSyncExternalStore. The payment flows (flows.ts) are plain async functions that read the
 * latest state with getState() after each await, which a component's own useReducer state can't
 * give them.
 *
 * One store per widget: _app.page.tsx keeps one for the widget pane for the whole visit (so it
 * remembers the donor's choices across page navigation), and every inline widget block gets its own.
 */
export type WidgetStore = {
  getState: () => State;
  dispatch: (action: WidgetAction) => void;
  subscribe: (listener: () => void) => () => void;
  /** Flows running now, by name (flows.ts `leading`) */
  inFlight: Set<string>;
  /** The latest run of each flow whose older runs' results are dropped (flows.ts `latest`) */
  runs: Map<string, number>;
};
export const createWidgetStore = (initialState: State = createInitialState()): WidgetStore => {
  let state = initialState;
  const listeners = new Set<() => void>();
  return {
    getState: () => state,
    dispatch: action => {
      const next = widgetReducer(state, action);
      if (next === state) return;
      state = next;
      listeners.forEach(listener => listener());
    },
    subscribe: listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    inFlight: new Set(),
    runs: new Map()
  };
};
const WidgetStoreContext = createContext<WidgetStore | null>(null);

/** Provides `store`, or a new store of its own when none is given (an inline widget) */
export const WidgetStoreProvider: React.FC<{
  store?: WidgetStore;
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(5);
  const {
    store,
    children
  } = t0;
  let t1;
  if ($[0] !== store) {
    t1 = () => store ?? createWidgetStore();
    $[0] = store;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [ownStore] = useState(t1);
  const t2 = store ?? ownStore;
  let t3;
  if ($[2] !== children || $[3] !== t2) {
    t3 = <WidgetStoreContext.Provider value={t2}>{children}</WidgetStoreContext.Provider>;
    $[2] = children;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
export const useWidgetStore = () => {
  const store = useContext(WidgetStoreContext);
  if (!store) {
    throw new Error("The donation widget needs a WidgetStoreProvider");
  }
  return store;
};
export const useWidgetDispatch = () => {
  return useWidgetStore().dispatch;
};

/**
 * Reads from the widget's state and re-renders when that part changes. The selector must return
 * a part of the state or a primitive, not a new object or array (default those outside it).
 */
export const useWidgetSelector = selector => {
  const $ = _c(3);
  const store = useWidgetStore();
  let t0;
  if ($[0] !== selector || $[1] !== store) {
    t0 = () => selector(store.getState());
    $[0] = selector;
    $[1] = store;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const snapshot = t0;
  return useSyncExternalStore(store.subscribe, snapshot, snapshot);
};

/**
 * The donation's total with the operations cut, as registered (for the payment panes and the
 * analytics events). Only for panes shown after the cause areas have loaded.
 */
export const useDonationTotal = () => {
  const $ = _c(5);
  const donation = useWidgetSelector(_temp);
  const causeAreas = useWidgetSelector(_temp2);
  let t0;
  if ($[0] !== causeAreas) {
    t0 = causeAreas ?? [];
    $[0] = causeAreas;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== donation || $[3] !== t0) {
    t1 = breakdownOf(donation, t0);
    $[2] = donation;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1.totalAmount;
};
function _temp(state) {
  return state.donation;
}
function _temp2(state_0) {
  return state_0.layout.causeAreas;
}